import { Fragment } from 'react';

import { Link, useMatches } from '@tanstack/react-router';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { Skeleton } from '~/components/ui/skeleton';
import { useConnectionQuery } from '~/hooks/useConnectionQuery';
import { useTopicQuery } from '~/hooks/useTopicQuery';
import { cn } from '~/lib/utils';
import { getConnectionName } from '~/utils/configAccessors';

interface BreadcrumbItemData {
  label: string;
  /** `undefined` while the crumb's name is still loading; renders a Skeleton. */
  isLoading?: boolean;
  path?: string;
  // Location hash to apply on navigation. TanStack Router builds the hash from
  // this option, not from a `#` embedded in `path`, so it lives separately.
  hash?: string;
}

// Full path templates for the matched routes that produce breadcrumbs. The
// match chain from useMatches() exposes each route's resolved `fullPath`, so the
// breadcrumb trail derives from the structured match data rather than from
// splitting the raw pathname string.
const TOPIC_DETAIL_PATH = '/topics/$topicId';
const CONNECTION_DETAIL_PATH = '/topics/$topicId/connections/$connectionId';
const PROFILE_PATH = '/profile';
const NOTIFICATIONS_PATH = '/notifications';

// Shared terminal-style type treatment for every crumb (mono, 13px, medium).
const CRUMB_TEXT = 'font-mono text-[13px] font-medium tracking-[0.02em]';

export function Breadcrumbs() {
  const matches = useMatches();

  // The leaf match identifies the active page; its params carry the route's
  // path variables. Reading from the match chain replaces manual pathname
  // parsing while preserving the exact breadcrumb output.
  const leafMatch = matches[matches.length - 1];
  const leafFullPath = leafMatch?.fullPath;
  const leafParams = (leafMatch?.params ?? {}) as {
    topicId?: string;
    connectionId?: string;
  };

  const topicId = leafParams.topicId;
  const connectionId = leafParams.connectionId;

  const { data: topicData } = useTopicQuery(topicId);
  const { data: connectionData } = useConnectionQuery(topicId, connectionId);

  const breadcrumbs = buildBreadcrumbs({
    leafFullPath,
    topicId,
    connectionId,
    topicName: topicData?.name,
    connectionName: connectionData ? getConnectionName(connectionData) : undefined,
  });

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <Breadcrumb className="mb-6">
      <BreadcrumbList>
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const isFirst = index === 0;

          return (
            <Fragment key={crumb.path ?? crumb.label}>
              {index > 0 && <BreadcrumbSeparator className={CRUMB_TEXT}>/</BreadcrumbSeparator>}
              <BreadcrumbItem>
                {crumb.isLoading ? (
                  <Skeleton className="h-[13px] w-16" />
                ) : isLast || !crumb.path ? (
                  <BreadcrumbPage className={cn(CRUMB_TEXT, 'text-foreground')}>
                    {crumb.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    render={
                      <Link
                        to={crumb.path}
                        hash={crumb.hash}
                        className={cn(
                          CRUMB_TEXT,
                          'cursor-pointer text-muted-foreground hover:text-primary hover:underline',
                          isFirst && "before:opacity-50 before:content-['//_']"
                        )}
                      >
                        {crumb.label}
                      </Link>
                    }
                  />
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

interface BuildBreadcrumbsArgs {
  leafFullPath: string | undefined;
  topicId: string | undefined;
  connectionId: string | undefined;
  topicName: string | undefined;
  connectionName: string | undefined;
}

/**
 * Reconstructs the breadcrumb trail from the matched route's full path template.
 * Each known route maps to a fixed trail: Topics is always the root crumb, the
 * topic crumb links to the topic detail page (with the #connections hash when a
 * connection is in view), and the connection crumb is the terminal label.
 */
function buildBreadcrumbs({
  leafFullPath,
  topicId,
  connectionId,
  topicName,
  connectionName,
}: BuildBreadcrumbsArgs): BreadcrumbItemData[] {
  if (!leafFullPath) {
    return [];
  }

  // Static routes: Topics root plus a single static label.
  if (leafFullPath === NOTIFICATIONS_PATH) {
    return [{ label: 'Topics', path: '/' }, { label: 'Notifications' }];
  }

  if (leafFullPath === PROFILE_PATH) {
    return [{ label: 'Topics', path: '/' }, { label: 'Profile' }];
  }

  // Topic and connection routes both begin with the Topics root crumb followed
  // by the topic crumb. At connection depth the topic link carries the
  // #connections hash so the topic page reopens on the connections tab.
  if (leafFullPath === CONNECTION_DETAIL_PATH && topicId && connectionId) {
    return [
      { label: 'Topics', path: '/' },
      {
        label: topicName ?? '',
        isLoading: !topicName,
        path: `/topics/${topicId}`,
        hash: 'connections',
      },
      { label: connectionName ?? '', isLoading: !connectionName },
    ];
  }

  if (leafFullPath === TOPIC_DETAIL_PATH && topicId) {
    return [
      { label: 'Topics', path: '/' },
      { label: topicName ?? '', isLoading: !topicName, path: `/topics/${topicId}` },
    ];
  }

  return [];
}
